/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import PropTypes from 'prop-types'
import React, { lazy, Suspense } from 'react'

import colors from '@sportninja/common/constants/appColors'
import LoadingSpinner from '../LoadingSpinner'
import { paragraphSmall, weightSemiBold } from '../css'

/**
 * Directions icon SVG component
 */
const DirectionsIcon = ({
  width = 24,
  height = 24,
  fill = colors.PRIMARY_300,
}) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={width}
    height={height}
    viewBox='0 0 24 24'
    fill='none'
  >
    <path
      d='M22.4274 10.5931L13.4174 1.5831C12.6674 0.833102 11.3474 0.823102 10.5874 1.5831L1.58744 10.5831C0.807441 11.3631 0.807441 12.6231 1.58744 13.4031L10.5874 22.4031C10.9774 22.7931 11.4874 22.9831 11.9974 22.9831C12.5074 22.9831 13.0174 22.7931 13.4074 22.4031L22.3974 13.4131C23.1874 12.6531 23.1974 11.3931 22.4274 10.5931ZM12.0074 20.9931L3.00744 11.9931L12.0074 2.9931L21.0074 11.9931L12.0074 20.9931ZM7.99744 11.0031V15.0031H9.99744V12.0031H13.9974V14.5031L17.4974 11.0031L13.9974 7.5031V10.0031H8.99744C8.44744 10.0031 7.99744 10.4531 7.99744 11.0031Z'
      fill={fill}
    />
  </svg>
)

const Map = lazy(() => import('../MapCard/Map'))

const GameLocationCard = ({
  venue,
  facility,
  title = 'LOCATION',
  isMobile = false,
}) => {
  if (!venue) {
    return null
  }

  /**
   * Handles the "Get directions" click to open Google Maps
   */
  const handleGetDirections = () => {
    if (venue.addressString) {
      const encodedAddress = encodeURIComponent(venue.addressString)
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div
      css={css`
        align-self: stretch;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: inline-flex;
      `}
    >
      {/* Header with title and Get directions link */}
      <div
        css={css`
          align-self: stretch;
          justify-content: space-between;
          align-items: center;
          display: inline-flex;
        `}
      >
        <div
          css={css`
            ${paragraphSmall}
            ${weightSemiBold}
            justify-content: center;
            display: flex;
            flex-direction: column;
            color: ${colors.NEUTRAL_50};
            text-transform: uppercase;
            line-height: 16.8px;
            letter-spacing: 1.68px;
            word-wrap: break-word;
          `}
        >
          {title}
        </div>
        {venue.addressString && (
          <div
            css={css`
              text-align: center;
              justify-content: center;
              display: flex;
              flex-direction: column;
              color: ${colors.PRIMARY_300};
              font-size: 12px;
              font-family: 'Inter', sans-serif;
              font-weight: 500;
              line-height: 17.4px;
              cursor: pointer;
              &:hover {
                text-decoration: underline;
              }
            `}
            onClick={handleGetDirections}
          >
            Get Directions
          </div>
        )}
      </div>

      {/* Main content card */}
      <div
        css={css`
          align-self: stretch;
          padding-right: 16px;
          background: ${colors.SECONDARY_300};
          overflow: hidden;
          border-radius: 8px;
          border: 1px solid ${colors.SECONDARY_200};
          justify-content: flex-start;
          align-items: stretch;
          gap: 16px;
          display: inline-flex;
          min-height: 72px;
        `}
      >
        {/* Map/Image section */}
        <div
          css={css`
            width: 72px;
            height: 72px;
            position: relative;
            overflow: hidden;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${colors.NEUTRAL_100};
            border-radius: 4px 0 0 4px;
          `}
        >
          {venue.coords ? (
            <Suspense
              fallback={
                <LoadingSpinner
                  css={css`
                    width: 24px;
                    height: 24px;
                  `}
                />
              }
            >
              <Map
                position={venue.coords}
                zoom={15}
                scrollWheelZoom={false}
                zoomControl={false}
                css={css`
                  width: 100%;
                  height: 100%;
                  border-radius: 4px 0 0 4px;

                  .leaflet-pane,
                  .leaflet-top,
                  .leaflet-control {
                    z-index: unset;
                  }

                  .leaflet-marker-icon {
                    font-size: 20px;
                    color: ${colors.PRIMARY};
                  }

                  .leaflet-bottom {
                    display: none;
                  }

                  .leaflet-touch .leaflet-bar a {
                    width: 20px;
                    height: 20px;
                    line-height: 16px;
                  }
                `}
              />
            </Suspense>
          ) : (
            <img
              src={'/images/generic_placeholders/placeholder_venue.png'}
              css={css`
                width: 72px;
                height: 72px;
                object-fit: cover;
                background: transparent;
              `}
            />
          )}
        </div>

        {/* Text content */}
        <div
          css={css`
            flex: 1 1 0;
            padding-top: 8px;
            padding-bottom: 8px;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            display: inline-flex;
            gap: 2px;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              color: ${colors.WHITE};
              font-size: 14px;
              font-family: 'Inter', Arial, Helvetica, sans-serif;
              font-weight: 700;
              line-height: 20px;
              word-wrap: break-word;
            `}
          >
            {venue.fullName}
            {facility && ` - ${facility}`}
          </div>
          <div
            css={css`
              align-self: stretch;
              color: ${colors.NEUTRAL_50};
              font-size: 12px;
              font-family: 'Inter', Arial, Helvetica, sans-serif;
              font-weight: 400;
              line-height: 17px;
              word-wrap: break-word;
            `}
          >
            {venue.addressString}
          </div>
        </div>

        {/* Directions icon */}
        <div
          css={css`
            width: 24px;
            height: 24px;
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: opacity 0.2s ease-in-out;
            align-self: center;
            flex-shrink: 0;

            &:hover {
              opacity: 0.8;
            }
          `}
          onClick={handleGetDirections}
          title='Get directions'
        >
          <DirectionsIcon width={24} height={24} fill={colors.PRIMARY_300} />
        </div>
      </div>
    </div>
  )
}

GameLocationCard.propTypes = {
  venue: PropTypes.shape({
    fullName: PropTypes.string,
    addressString: PropTypes.string,
    coords: PropTypes.array,
  }).isRequired,
  facility: PropTypes.string,
  title: PropTypes.string,
}

export default GameLocationCard
